import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import Cookies from 'js-cookie';
import BACKEND_URL from '../config';
import Input from './ui/Input';
import Button from './ui/Button';
import Modal from './ui/Modal';
import { IconX, IconPlus, IconEdit, IconDeviceFloppy, IconArrowBackUp, IconLoader2, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { FullPdfViewerLazy } from './ui/FullPDFViewerLazy';
import { Phase, Role } from '../types';
import TagMultiSelect from './ui/TagMultiSelect';
import DocumentTextModal from './DocumentTextModal';

interface Document {
    id: string;
    name: string;
    raw_document_presigned_url: string;
    title: string;
    content: string;
    url: string;
}

interface Documentation {
    id: number;
    document: Document;
    relevance: number;
    characteristic_data: Record<string, string[]>;
    summary: string;
    name: string;
    phases?: Phase[];
    roles?: Role[];
}

interface Comment {
    id: number;
    comment: string;
    created_at: string;
    created_by: number;
}

interface CharacteristicFilterRow {
    slug: string;
    value: string;
}

interface DocumentsListProps {
    characteristics: Record<string, any>;
    // Kept for backwards-compat with callers passing the parent's preload.
    // DocumentsList now owns its own paginated fetching and ignores these.
    initialDocumentation?: Documentation[];
    loading?: boolean;
}

const PAGE_SIZE = 20;

interface DocumentationPage {
    count: number;
    next: string | null;
    previous: string | null;
    results: Documentation[];
}

interface DocumentationItemProps {
    doc: Documentation;
    characteristics: Record<string, any>;
    updateDocumentation: (updatedDoc: Documentation) => void;
    availablePhases: Phase[];
    availableRoles: Role[];
}

interface AddCharacteristicToDocumentationProps {
    doc: Documentation;
    characteristics: Record<string, any>;
    updateDocumentation: (updatedDoc: Documentation) => void;
}

const CommentSection = ({ comments, loading }: { comments: Comment[], loading: boolean }) => (
    <div className="mt-4 space-y-2">
        {loading ? (
            <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
        ) : (
            comments.map((comment) => (
                <div key={comment.id} className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">{comment.comment}</p>
                    <p className="text-xs text-gray-500 mt-1">
                        {new Date(comment.created_at).toLocaleString()}
                    </p>
                </div>
            ))
        )}
    </div>
);

const AddCharacteristicToDocumentation = ({ doc, characteristics, updateDocumentation }: AddCharacteristicToDocumentationProps) => {
    const [characteristic, setCharacteristic] = useState<string>('');
    const [answer, setAnswer] = useState<string[]>([]);
    const [openModal, setOpenModal] = useState(false);

    const handleAddCharacteristic = async () => {
        const response = await fetch(`${BACKEND_URL}/vnog/add_characteristic_to_documentation/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'X-CSRFToken': Cookies.get('csrftoken') || '',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                documentation_id: doc.id.toString(),
                characteristic_slug: characteristic,
                answer: answer,
            }),
        });

        if (response.ok) {
            const data = await response.json();
            toast.success('Characteristic added successfully');
            updateDocumentation(data);
        } else {
            toast.error('Failed to add characteristic');
        }
    };

    const renderCharacteristicInput = () => {
        if (characteristics[characteristic]?.type === 'Vrije tekst') {
            return <Input label="Answer" type="text" value={answer.join(', ')} onChange={(value) => setAnswer([value])} />
        } else if (characteristics[characteristic]?.type === 'Getal') {
            return <Input label="Answer" type="number" value={answer[0] || ''} onChange={(value) => setAnswer([value])} />
        } else if (characteristics[characteristic]?.type === 'Ja/Nee') {
            return <select className="w-full p-2 rounded-md border border-gray-300" value={answer[0] || ''} onChange={(e) => setAnswer([e.target.value])}>
                <option value="">Select an answer</option>
                <option value="Ja">Ja</option>
                <option value="Nee">Nee</option>
            </select>
        } else if (characteristics[characteristic]?.type === "Meervoudige opsom" || characteristics[characteristic]?.type === "Enkelvoudige opsom") {
            const allOptions = characteristics[characteristic]?.answer_options || [];
            const isAllSelected = allOptions.length > 0 && allOptions.every((option: string) => answer.includes(option));

            const handleSelectAll = () => {
                if (isAllSelected) {
                    setAnswer([]);
                } else {
                    setAnswer([...allOptions]);
                }
            };

            return (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">
                            {characteristics[characteristic]?.type === "Meervoudige opsom" ? "Select answers (multiple)" : "Select answer (single)"}
                        </label>
                        <button
                            type="button"
                            onClick={handleSelectAll}
                            className="text-sm text-blue-600 hover:text-blue-800 underline"
                        >
                            {isAllSelected ? "Deselect All" : "Select All"}
                        </button>
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                        {allOptions.map((option: string) => (
                            <label key={option} className="flex items-center space-x-2 py-1">
                                <input
                                    type="checkbox"
                                    checked={answer.includes(option)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setAnswer([...answer, option]);
                                        } else {
                                            setAnswer(answer.filter(item => item !== option));
                                        }
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            );
        } else {
            return <div>Unknown characteristic type</div>
        }
    }

    return (
        <div>
            <Modal isOpen={openModal} onRequestClose={() => setOpenModal(false)}>
                <div className="w-[90vw] h-[90vh] bg-white rounded-lg flex flex-col">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h2 className="text-xl font-semibold">Add Characteristic</h2>
                        <button
                            onClick={() => setOpenModal(false)}
                            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <IconX size={24} stroke={1.5} />
                        </button>
                    </div>
                    <div className="flex flex-col gap-4 p-4">
                        <select className="w-full p-2 rounded-md border border-gray-300" onChange={(e) => {
                            setCharacteristic(e.target.value);
                            setAnswer([]);
                        }}>
                            <option value="">Select a characteristic</option>
                            {Object.keys(characteristics).map((characteristic) => (
                                <option key={characteristic} value={characteristic}>{characteristic}</option>
                            ))}
                        </select>
                        {
                            characteristic && renderCharacteristicInput()
                        }
                    </div>
                    <div className="flex justify-end p-4 border-t">
                        <Button className="disabled:opacity-50 w-fit text-sm" onClick={handleAddCharacteristic} disabled={!characteristic || answer.length === 0}>Submit</Button>
                    </div>
                    <hr className="my-4" />
                    { characteristics[characteristic]?.explanation && <h4 className="text-sm font-medium text-gray-700 mb-3 p-4">Explanation</h4>}
                    <div className="flex flex-col gap-2 text-sm">
                        {
                            characteristics[characteristic]?.explanation && (
                                <div className="text-sm text-gray-500">
                                    {characteristics[characteristic].explanation}
                                </div>
                            )
                        }
                    </div>
                </div>
            </Modal>
            <Button className="w-fit text-sm" onClick={() => setOpenModal(true)}>
                Voeg een classificatie toe
            </Button>
        </div>
    );
};

const PhaseRoleEditor = ({
    doc,
    availablePhases,
    availableRoles,
    onSaved,
    onCancel,
}: {
    doc: Documentation;
    availablePhases: Phase[];
    availableRoles: Role[];
    onSaved: (updated: Documentation) => void;
    onCancel: () => void;
}) => {
    const initialPhaseIds = useMemo(() => (doc.phases || []).map(p => p.id), [doc.phases]);
    const initialRoleIds = useMemo(() => (doc.roles || []).map(r => r.id), [doc.roles]);
    const [selectedPhaseIds, setSelectedPhaseIds] = useState<number[]>(initialPhaseIds);
    const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>(initialRoleIds);
    const [isSaving, setIsSaving] = useState(false);

    const sameIds = (a: number[], b: number[]) => {
        if (a.length !== b.length) return false;
        const setA = new Set(a);
        return b.every(x => setA.has(x));
    };

    const unchanged = sameIds(selectedPhaseIds, initialPhaseIds) && sameIds(selectedRoleIds, initialRoleIds);
    const empty = selectedPhaseIds.length === 0 || selectedRoleIds.length === 0;

    const handleSave = async () => {
        if (empty || unchanged || isSaving) return;
        setIsSaving(true);
        try {
            const body = new URLSearchParams({
                phase_ids: JSON.stringify(selectedPhaseIds),
                role_ids: JSON.stringify(selectedRoleIds),
            });
            const response = await fetch(`${BACKEND_URL}/vnog/documentation/${doc.id}/set_phases_roles/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body,
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                toast.error(err?.error || 'Failed to update phases/roles');
                return;
            }
            const updated = await response.json();
            toast.success('Phases en rollen opgeslagen');
            onSaved(updated);
        } catch (e) {
            toast.error('Failed to update phases/roles');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="mt-3 border rounded-lg p-3 bg-gray-50">
            <div className="space-y-3">
                <TagMultiSelect
                    label="Phases"
                    options={availablePhases}
                    selectedIds={selectedPhaseIds}
                    onChange={setSelectedPhaseIds}
                />
                <TagMultiSelect
                    label="Roles"
                    options={availableRoles}
                    selectedIds={selectedRoleIds}
                    onChange={setSelectedRoleIds}
                />
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
                <button
                    type="button"
                    onClick={onCancel}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                    <IconArrowBackUp size={14} stroke={1.5} />
                    Annuleren
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={empty || unchanged || isSaving}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <IconDeviceFloppy size={14} stroke={1.5} />
                    {isSaving ? 'Opslaan…' : 'Opslaan'}
                </button>
            </div>
        </div>
    );
};

const DocumentationItem = ({
    doc,
    characteristics,
    updateDocumentation,
    availablePhases,
    availableRoles,
}: DocumentationItemProps) => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [newComment, setNewComment] = useState('');
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [hasFetchedComments, setHasFetchedComments] = useState(false);
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
    const [isTextModalOpen, setIsTextModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState<string | null>(null);
    const [isEditingTags, setIsEditingTags] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);

    const phaseChips = doc.phases || [];
    const roleChips = doc.roles || [];

    const fetchComments = async (force = false) => {
        if (hasFetchedComments && !force) return;
        
        setCommentsLoading(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/document-comments/list/?document_id=${doc.id}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
            });
            const data = await response.json();
            setComments(data);
            setHasFetchedComments(true);
        } catch (error) {
            console.error('Error fetching document comments:', error);
            toast.error('Failed to fetch document comments');
        } finally {
            setCommentsLoading(false);
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    fetchComments();
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );

        if (itemRef.current) {
            observer.observe(itemRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [doc.id]);

    const openPdfModal = async () => {
        setIsPdfModalOpen(true);
        if (pdfUrl || !doc.document?.id) return;
        setPdfLoading(true);
        setPdfError(null);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/documents/${doc.document.id}/presigned_url/`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' },
            });
            if (!response.ok) {
                setPdfError(`Kon PDF niet ophalen (status ${response.status}).`);
                return;
            }
            const data = await response.json();
            setPdfUrl(data?.raw_document_presigned_url || null);
        } catch (e) {
            setPdfError(e instanceof Error ? e.message : 'Onbekende fout bij ophalen PDF URL.');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleCommentSubmit = async () => {
        if (!newComment.trim()) return;

        try {
            const response = await fetch(`${BACKEND_URL}/vnog/document-comments/create/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    document_id: doc.id.toString(),
                    comment: newComment,
                }),
            });

            if (response.ok) {
                setNewComment('');
                fetchComments(true);
                toast.success('Comment added successfully');
            } else {
                throw new Error('Failed to add comment');
            }
        } catch (error) {
            console.error('Error adding document comment:', error);
            toast.error('Failed to add comment');
        }
    };

    const handleDeleteCharacteristic = async (key: string, value: string[]) => {
        const response = await fetch(`${BACKEND_URL}/vnog/delete_characteristic_from_documentation/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'X-CSRFToken': Cookies.get('csrftoken') || '',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                documentation_id: doc.id.toString(),
                characteristic_slug: key,
                answer: value,
            }),
        });

        if (response.ok) {
            const data = await response.json();
            updateDocumentation(data);
            toast.success('Characteristic deleted successfully');
        } else {
            toast.error('Failed to delete characteristic');
        }
    };

    return (
        <div ref={itemRef} className="border rounded-lg p-4">
            <div className="flex justify-between items-start mb-2 gap-4">
                <div className="min-w-0">
                    <h3 className="font-medium text-lg">{doc.document?.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">Relevance: {doc.relevance}</p>
                    <p className="text-sm text-gray-600 mt-1">{doc.summary}</p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                    <button
                        onClick={openPdfModal}
                        className="text-blue-700 hover:text-blue-900 text-sm bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded whitespace-nowrap"
                    >
                        View PDF
                    </button>
                    <button
                        onClick={() => setIsTextModalOpen(true)}
                        className="text-emerald-700 hover:text-emerald-900 text-sm bg-emerald-50 hover:bg-emerald-100 px-3 py-1 rounded whitespace-nowrap"
                    >
                        View parsed text
                    </button>
                </div>
            </div>

            {/* Phase/role chip strip */}
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {phaseChips.length === 0 && roleChips.length === 0 && (
                    <span className="text-xs text-gray-500 italic">Geen phases of rollen gekoppeld.</span>
                )}
                {phaseChips.map(p => (
                    <span
                        key={`phase-${p.id}`}
                        title={p.description || p.slug}
                        className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                    >
                        Phase: {p.slug}
                    </span>
                ))}
                {roleChips.map(r => (
                    <span
                        key={`role-${r.id}`}
                        title={r.description || r.slug}
                        className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200"
                    >
                        Role: {r.slug}
                    </span>
                ))}
                <button
                    type="button"
                    onClick={() => setIsEditingTags(v => !v)}
                    className="ml-2 inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 underline"
                >
                    <IconEdit size={12} stroke={1.5} />
                    {isEditingTags ? 'Sluit bewerker' : 'Bewerken'}
                </button>
            </div>

            {isEditingTags && (
                <PhaseRoleEditor
                    doc={doc}
                    availablePhases={availablePhases}
                    availableRoles={availableRoles}
                    onSaved={(updated) => {
                        updateDocumentation(updated);
                        setIsEditingTags(false);
                    }}
                    onCancel={() => setIsEditingTags(false)}
                />
            )}

            {/* Characteristics Section */}
            <div className="mt-4">
                <h4 className="flex gap-4 text-lg font-medium text-gray-700 mb-3">
                    Meldingsclassificaties (LMC) ({Object.keys(doc.characteristic_data).length})
                    <AddCharacteristicToDocumentation doc={doc} characteristics={characteristics} updateDocumentation={updateDocumentation} />
                    </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(doc.characteristic_data).map(([key, value]) => (
                        <div key={key} className="bg-gray-50 rounded-lg p-3 relative">
                            <div className="absolute top-0 right-0 rounded-full bg-gray-100 p-1 cursor-pointer" onClick={() => handleDeleteCharacteristic(key, value)}>
                                <IconX size={16} stroke={1.5} />
                            </div>
                            <div className="flex items-start justify-between">
                                <div>
                                    <h5 className="font-medium text-sm text-gray-900">{key}</h5>
                                    <p className="text-sm text-gray-600 mt-1">{value.join(', ')}</p>
                                </div>
                                {characteristics[key]?.explanation && (
                                    <div className="ml-2">
                                        <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                            {characteristics[key].explanation}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Document Comments Section */}
            <div className="mt-4 border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Feedback</h4>
                <CommentSection comments={comments} loading={commentsLoading} />
                <div className="flex flex-col gap-2">
                    <Input
                        label="Voeg feedback toe"
                        value={newComment}
                        onChange={setNewComment}
                        id={doc.document.id}
                        type="document"
                        onSubmit={handleCommentSubmit}
                        placeholder="Schrijf hier je feedback over dit document en/of de verebonden classificaties"
                    />
                    <Button className="w-fit" onClick={handleCommentSubmit}>Voeg Feedback Toe</Button>
                </div>
            </div>

            {/* PDF Preview Modal */}
            <Modal isOpen={isPdfModalOpen} onRequestClose={() => setIsPdfModalOpen(false)}>
                <div className="w-[90vw] h-[90vh] bg-white rounded-lg flex flex-col">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h2 className="text-xl font-semibold">{doc.document?.name}</h2>
                        <button
                            onClick={() => setIsPdfModalOpen(false)}
                            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <IconX size={24} stroke={1.5} />
                        </button>
                    </div>
                    <div className="flex-1 p-4 overflow-y-auto">
                        {pdfLoading && (
                            <div className="flex items-center justify-center h-full text-gray-500">PDF laden…</div>
                        )}
                        {!pdfLoading && pdfError && (
                            <div className="flex items-center justify-center h-full text-gray-500">{pdfError}</div>
                        )}
                        {!pdfLoading && !pdfError && pdfUrl && (
                            <FullPdfViewerLazy pdfUrl={pdfUrl} />
                        )}
                    </div>
                </div>
            </Modal>

            {/* Parsed-text Modal */}
            <DocumentTextModal
                isOpen={isTextModalOpen}
                onClose={() => setIsTextModalOpen(false)}
                documentId={doc.document?.id || null}
                documentName={doc.document?.name}
            />
        </div>
    );
};

// ---------- Filter bar ----------

const CharacteristicFilterRowEditor = ({
    row,
    characteristics,
    onChange,
    onRemove,
}: {
    row: CharacteristicFilterRow;
    characteristics: Record<string, any>;
    onChange: (next: CharacteristicFilterRow) => void;
    onRemove: () => void;
}) => {
    const slug = row.slug;
    const cfg = slug ? characteristics[slug] : undefined;
    const type = cfg?.type;

    const renderValueInput = () => {
        if (!slug) return null;
        if (type === 'Vrije tekst' || type === 'Datum') {
            return (
                <input
                    type="text"
                    placeholder="Waarde bevat… (optioneel)"
                    value={row.value}
                    onChange={e => onChange({ ...row, value: e.target.value })}
                    className="flex-1 min-w-[160px] rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
            );
        }
        if (type === 'Getal') {
            return (
                <input
                    type="number"
                    placeholder="Waarde (optioneel)"
                    value={row.value}
                    onChange={e => onChange({ ...row, value: e.target.value })}
                    className="flex-1 min-w-[120px] rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
            );
        }
        if (type === 'Ja/Nee') {
            return (
                <select
                    value={row.value}
                    onChange={e => onChange({ ...row, value: e.target.value })}
                    className="flex-1 min-w-[120px] rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                    <option value="">Elke waarde</option>
                    <option value="Ja">Ja</option>
                    <option value="Nee">Nee</option>
                </select>
            );
        }
        if (type === 'Meervoudige opsom' || type === 'Enkelvoudige opsom') {
            const opts: string[] = cfg?.answer_options || [];
            return (
                <select
                    value={row.value}
                    onChange={e => onChange({ ...row, value: e.target.value })}
                    className="flex-1 min-w-[160px] rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                    <option value="">Elke waarde</option>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        }
        return (
            <input
                type="text"
                placeholder="Waarde (optioneel)"
                value={row.value}
                onChange={e => onChange({ ...row, value: e.target.value })}
                className="flex-1 min-w-[160px] rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
        );
    };

    return (
        <div className="flex items-center gap-2">
            <select
                value={row.slug}
                onChange={e => onChange({ slug: e.target.value, value: '' })}
                className="min-w-[200px] rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
                <option value="">Kies een classificatie…</option>
                {Object.keys(characteristics).sort().map(name => (
                    <option key={name} value={name}>{name}</option>
                ))}
            </select>
            {renderValueInput()}
            <button
                type="button"
                onClick={onRemove}
                aria-label="Verwijder filter"
                className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            >
                <IconX size={16} stroke={1.5} />
            </button>
        </div>
    );
};

const DocumentFilterBar = ({
    queryInput,
    setQueryInput,
    availablePhases,
    availableRoles,
    selectedPhaseIds,
    setSelectedPhaseIds,
    selectedRoleIds,
    setSelectedRoleIds,
    characteristicFilters,
    setCharacteristicFilters,
    characteristics,
    searching,
    onClearAll,
    hasActiveFilters,
}: {
    queryInput: string;
    setQueryInput: (s: string) => void;
    availablePhases: Phase[];
    availableRoles: Role[];
    selectedPhaseIds: number[];
    setSelectedPhaseIds: (ids: number[]) => void;
    selectedRoleIds: number[];
    setSelectedRoleIds: (ids: number[]) => void;
    characteristicFilters: CharacteristicFilterRow[];
    setCharacteristicFilters: (rows: CharacteristicFilterRow[]) => void;
    characteristics: Record<string, any>;
    searching: boolean;
    onClearAll: () => void;
    hasActiveFilters: boolean;
}) => {
    const addRow = () => setCharacteristicFilters([...characteristicFilters, { slug: '', value: '' }]);
    const updateRow = (i: number, next: CharacteristicFilterRow) => {
        const copy = characteristicFilters.slice();
        copy[i] = next;
        setCharacteristicFilters(copy);
    };
    const removeRow = (i: number) => {
        const copy = characteristicFilters.slice();
        copy.splice(i, 1);
        setCharacteristicFilters(copy);
    };

    return (
        <div className="border rounded-lg p-4 mb-4 bg-gray-50 space-y-4">
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={queryInput}
                    onChange={e => setQueryInput(e.target.value)}
                    placeholder="Zoek op naam of samenvatting…"
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {searching && (
                    <span className="text-xs text-gray-500">Zoeken…</span>
                )}
                {hasActiveFilters && (
                    <button
                        type="button"
                        onClick={onClearAll}
                        className="text-xs px-3 py-2 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                        Wis filters
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TagMultiSelect
                    label="Phases"
                    options={availablePhases}
                    selectedIds={selectedPhaseIds}
                    onChange={setSelectedPhaseIds}
                    requireAtLeastOne={false}
                />
                <TagMultiSelect
                    label="Roles"
                    options={availableRoles}
                    selectedIds={selectedRoleIds}
                    onChange={setSelectedRoleIds}
                    requireAtLeastOne={false}
                />
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Classificaties</label>
                    <button
                        type="button"
                        onClick={addRow}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    >
                        <IconPlus size={14} stroke={1.5} />
                        Voeg filter toe
                    </button>
                </div>
                {characteristicFilters.length === 0 ? (
                    <p className="text-xs text-gray-500 italic">Geen classificatie-filters actief.</p>
                ) : (
                    <div className="space-y-2">
                        {characteristicFilters.map((row, i) => (
                            <CharacteristicFilterRowEditor
                                key={i}
                                row={row}
                                characteristics={characteristics}
                                onChange={(next) => updateRow(i, next)}
                                onRemove={() => removeRow(i)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------- Main list ----------

const useDebounced = <T,>(value: T, delayMs: number): T => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const handle = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(handle);
    }, [value, delayMs]);
    return debounced;
};

const PaginationControls = ({
    currentPage,
    totalPages,
    totalCount,
    loading,
    onPageChange,
}: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    loading: boolean;
    onPageChange: (page: number) => void;
}) => {
    if (totalCount === 0) return null;
    const canPrev = currentPage > 1 && !loading;
    const canNext = currentPage < totalPages && !loading;
    return (
        <div className="flex items-center justify-between gap-3 py-2">
            <span className="text-xs text-gray-600">
                Pagina {currentPage} van {Math.max(1, totalPages)} — {totalCount} {totalCount === 1 ? 'document' : 'documenten'}
            </span>
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    onClick={() => onPageChange(1)}
                    disabled={!canPrev}
                    className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Eerste
                </button>
                <button
                    type="button"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={!canPrev}
                    aria-label="Vorige pagina"
                    className="inline-flex items-center text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <IconChevronLeft size={14} stroke={1.5} />
                    Vorige
                </button>
                <button
                    type="button"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={!canNext}
                    aria-label="Volgende pagina"
                    className="inline-flex items-center text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Volgende
                    <IconChevronRight size={14} stroke={1.5} />
                </button>
                <button
                    type="button"
                    onClick={() => onPageChange(totalPages)}
                    disabled={!canNext}
                    className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Laatste
                </button>
            </div>
        </div>
    );
};

const DocumentsLoader = ({ label }: { label: string }) => (
    <div className="flex items-center justify-center gap-3 py-12 text-gray-600">
        <IconLoader2 size={28} stroke={1.5} className="animate-spin" />
        <span className="text-sm">{label}</span>
    </div>
);

const DocumentsList = ({ characteristics }: DocumentsListProps) => {
    const [documents, setDocuments] = useState<Documentation[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [availablePhases, setAvailablePhases] = useState<Phase[]>([]);
    const [availableRoles, setAvailableRoles] = useState<Role[]>([]);

    // Filter state
    const [queryInput, setQueryInput] = useState('');
    const debouncedQuery = useDebounced(queryInput, 350);
    const [selectedPhaseIds, setSelectedPhaseIds] = useState<number[]>([]);
    const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
    const [characteristicFilters, setCharacteristicFilters] = useState<CharacteristicFilterRow[]>([]);

    const hasActiveFilters = useMemo(() => {
        if (debouncedQuery.trim().length > 0) return true;
        if (selectedPhaseIds.length > 0) return true;
        if (selectedRoleIds.length > 0) return true;
        return characteristicFilters.some(r => r.slug);
    }, [debouncedQuery, selectedPhaseIds, selectedRoleIds, characteristicFilters]);

    const totalPages = useMemo(
        () => (totalCount === 0 ? 0 : Math.ceil(totalCount / PAGE_SIZE)),
        [totalCount],
    );

    // Pre-fetch phases/roles catalogs once.
    useEffect(() => {
        fetch(`${BACKEND_URL}/vnog/phases/`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : [])
            .then((data: Phase[]) => setAvailablePhases(Array.isArray(data) ? data : []))
            .catch(() => {});
        fetch(`${BACKEND_URL}/vnog/roles/`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : [])
            .then((data: Role[]) => setAvailableRoles(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, []);

    const buildParams = useCallback((page: number) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('page_size', String(PAGE_SIZE));
        if (debouncedQuery.trim()) params.set('q', debouncedQuery.trim());
        if (selectedPhaseIds.length > 0) params.set('phase_ids', JSON.stringify(selectedPhaseIds));
        if (selectedRoleIds.length > 0) params.set('role_ids', JSON.stringify(selectedRoleIds));
        const activeCharFilters = characteristicFilters
            .filter(r => r.slug)
            .map(r => r.value ? { slug: r.slug, value: r.value } : { slug: r.slug });
        if (activeCharFilters.length > 0) {
            params.set('characteristic_filters', JSON.stringify(activeCharFilters));
        }
        return params;
    }, [debouncedQuery, selectedPhaseIds, selectedRoleIds, characteristicFilters]);

    // Reset to page 1 whenever the filter inputs change. We track the inputs
    // (not `currentPage`) so manually navigating pages doesn't reset itself.
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedQuery, selectedPhaseIds, selectedRoleIds, characteristicFilters]);

    // Fetch the current page whenever filters or the active page change.
    useEffect(() => {
        let cancelled = false;
        const params = buildParams(currentPage);
        const url = `${BACKEND_URL}/vnog/get_all_documentation/?${params.toString()}`;

        setLoading(true);
        setError(null);
        fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' },
        })
            .then(async (response) => {
                if (cancelled) return;
                if (response.status === 404 && currentPage > 1) {
                    // Out-of-range page after filter change; reset to page 1.
                    setCurrentPage(1);
                    return;
                }
                if (!response.ok) {
                    throw new Error(`Documentation fetch failed (status ${response.status})`);
                }
                const data: DocumentationPage = await response.json();
                if (cancelled) return;
                setDocuments(data.results || []);
                setTotalCount(typeof data.count === 'number' ? data.count : (data.results?.length || 0));
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                console.error('Documentation page fetch failed', e);
                const msg = e instanceof Error ? e.message : 'Onbekende fout';
                setError(msg);
                toast.error('Kon documenten niet ophalen.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [currentPage, buildParams]);

    const updateDocumentation = (updatedDoc: Documentation) => {
        setDocuments(prev => prev.map(d => d.id === updatedDoc.id ? updatedDoc : d));
    };

    const clearAllFilters = () => {
        setQueryInput('');
        setSelectedPhaseIds([]);
        setSelectedRoleIds([]);
        setCharacteristicFilters([]);
    };

    const goToPage = (next: number) => {
        const clamped = Math.max(1, Math.min(next, Math.max(1, totalPages)));
        setCurrentPage(clamped);
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-xl font-semibold">Documenten</h2>
                <span className="text-xs text-gray-500">
                    {totalCount} {totalCount === 1 ? 'document' : 'documenten'}
                </span>
            </div>

            <DocumentFilterBar
                queryInput={queryInput}
                setQueryInput={setQueryInput}
                availablePhases={availablePhases}
                availableRoles={availableRoles}
                selectedPhaseIds={selectedPhaseIds}
                setSelectedPhaseIds={setSelectedPhaseIds}
                selectedRoleIds={selectedRoleIds}
                setSelectedRoleIds={setSelectedRoleIds}
                characteristicFilters={characteristicFilters}
                setCharacteristicFilters={setCharacteristicFilters}
                characteristics={characteristics}
                searching={loading}
                onClearAll={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
            />

            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                loading={loading}
                onPageChange={goToPage}
            />

            {loading ? (
                <DocumentsLoader label="Documenten laden…" />
            ) : error ? (
                <div className="text-sm text-red-600 py-8 text-center">
                    Fout bij ophalen documenten: {error}
                </div>
            ) : documents.length === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center">
                    {hasActiveFilters
                        ? 'Geen documenten gevonden voor de huidige filters.'
                        : 'Geen documenten beschikbaar.'}
                </div>
            ) : (
                <div className="space-y-6">
                    {documents.map((doc) => (
                        <DocumentationItem
                            key={doc.id}
                            doc={doc}
                            characteristics={characteristics}
                            updateDocumentation={updateDocumentation}
                            availablePhases={availablePhases}
                            availableRoles={availableRoles}
                        />
                    ))}
                </div>
            )}

            {!loading && documents.length > 0 && (
                <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    loading={loading}
                    onPageChange={goToPage}
                />
            )}
        </div>
    );
};

export default DocumentsList;
